import { useState, useEffect, useRef } from 'react';
import { X, Shield, AlertTriangle, DollarSign, Info, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocale } from '../../contexts/LocaleContext';

/* ─── Shared slider ─── */
function NativeSlider({ value, min, max, step, onChange, accentVar = '--accent-primary, #3b82f6' }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="relative flex items-center h-10">
      <div className="absolute left-0 right-0 h-[5px] rounded-full"
        style={{ background: 'var(--bg-tertiary, #e5e7eb)' }} />
      <div className="absolute left-0 h-[5px] rounded-full"
        style={{ width: `${pct}%`, background: `var(${accentVar})`, transition: 'width .05s' }} />
      <input type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        className="ov-thumb w-full h-10 absolute left-0 cursor-pointer bg-transparent appearance-none" />
    </div>
  );
}

/* ─── Editable value badge ─── */
function EditableBadge({ displayValue, rawValue, min, max, step, onCommit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    setDraft(rawValue !== null && rawValue !== '' ? String(rawValue) : '');
    setEditing(true);
    setTimeout(() => { inputRef.current?.select(); }, 0);
  };

  const commit = () => {
    const parsed = step % 1 === 0 ? parseInt(draft) : parseFloat(draft);
    if (!isNaN(parsed)) {
      const clamped = Math.min(max, Math.max(min, parsed));
      onCommit(clamped);
    }
    setEditing(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={min} max={max} step={step}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full text-center w-24 outline-none"
        style={{
          background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 10%, transparent)',
          color: 'var(--accent-primary, #3b82f6)',
          border: '1.5px solid var(--accent-primary, #3b82f6)',
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEdit}
      title="Click to type a value"
      className="text-sm font-mono font-semibold px-2 py-0.5 rounded-full transition-all active:scale-95"
      style={{
        background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 10%, transparent)',
        color: 'var(--accent-primary, #3b82f6)',
        border: '1.5px solid transparent',
        cursor: 'text',
      }}>
      {displayValue}
    </button>
  );
}

function SliderField({ label, hint, value, displayValue, sliderValue, min, max, step, onChange, onReset, accentVar, resetLabel, onDirectInput }) {
  return (
    <div className="py-3">
      <div className="flex items-center justify-between mb-1">
        <label className="text-[15px] font-medium" style={{ color: 'var(--text-primary, #111)' }}>{label}</label>
        <EditableBadge
          displayValue={displayValue}
          rawValue={value}
          min={min} max={max} step={step}
          onCommit={onDirectInput}
        />
      </div>
      <NativeSlider value={sliderValue} min={min} max={max} step={step} onChange={onChange} accentVar={accentVar} />
      {onReset && (
        <button type="button" onClick={onReset}
          className="mt-1 text-xs font-medium underline underline-offset-2 active:opacity-60"
          style={{ color: 'var(--accent-info, #0ea5e9)' }}>
          {resetLabel}
        </button>
      )}
      {hint && <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>{hint}</p>}
    </div>
  );
}

function ToggleRow({ checked, onChange, label, sublabel, danger }) {
  return (
    <>
      <div className="ov-toggle-row flex items-center justify-between py-4">
        <div>
          <p className="text-[15px] font-medium"
            style={{ color: danger ? 'var(--accent-danger, #ef4444)' : 'var(--text-primary, #111)' }}>
            {label}
          </p>
          {sublabel && <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary, #6b7280)' }}>{sublabel}</p>}
        </div>
        <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
          className="relative flex-shrink-0 w-[51px] h-[31px] rounded-full transition-all duration-200 active:scale-95"
          style={{ background: checked ? (danger ? 'var(--accent-danger,#ef4444)' : 'var(--accent-primary,#3b82f6)') : 'var(--bg-tertiary,#d1d5db)' }}>
          <span className="absolute top-[2px] left-[2px] w-[27px] h-[27px] rounded-full bg-white shadow transition-transform duration-200"
            style={{ transform: checked ? 'translateX(20px)' : 'translateX(0)' }} />
        </button>
      </div>
      <label className="ov-checkbox-row flex items-center gap-3 cursor-pointer p-3 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors">
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          className="w-5 h-5 rounded border-2 border-[var(--border-color)]"
          style={{ accentColor: danger ? 'var(--accent-danger)' : 'var(--accent-primary)' }} />
        <div>
          <span className="text-sm font-medium block"
            style={{ color: danger ? 'var(--accent-danger)' : 'var(--text-primary)' }}>
            {label}
          </span>
          {sublabel && <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{sublabel}</span>}
        </div>
      </label>
    </>
  );
}

/* ─── Map override → form ─── */
function mapOverrideToForm(override) {
  if (!override) return {
    maxLeverage: '', maxLotSize: '', minLotSize: '', lotStep: '',
    marginCallLevel: '', stopOutLevel: '', maxOpenPositions: '',
    commissionPerLot: '', tradingDisabled: 0,
  };
  return {
    maxLeverage:      override.maxLeverage      ?? '',
    maxLotSize:       override.maxLotSize       ?? '',
    minLotSize:       override.minLotSize       ?? '',
    lotStep:          override.lotStep          ?? '',
    marginCallLevel:  override.marginCallLevel  ?? '',
    stopOutLevel:     override.stopOutLevel     ?? '',
    maxOpenPositions: override.maxOpenPositions ?? '',
    commissionPerLot: override.commissionPerLot ?? '',
    tradingDisabled:  override.tradingDisabled  ?? 0,
  };
}

/* ─── Section content ─── */
function SectionContent({ activeSection, formData, set, t, minAboveMax, existingOverride }) {
  const gd = t('userOverride.groupDefault');

  if (activeSection === 'trading') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('userOverride.sectionTradingControl')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
        {t('userOverride.infoBannerDesc')}
      </p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <ToggleRow
          checked={formData.tradingDisabled === 1}
          onChange={v => set('tradingDisabled', v ? 1 : 0)}
          label={t('userOverride.disableTrading')}
          sublabel={t('userOverride.disableTradingSubLabel')}
          danger
        />
      </div>
    </div>
  );

  if (activeSection === 'risk') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('userOverride.sectionRiskParameters')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
        {t('userOverride.infoBannerDesc')}
      </p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderField
          label={t('userOverride.maxLeverage')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.maxLeverage}
          displayValue={formData.maxLeverage ? `1:${formData.maxLeverage}` : gd}
          sliderValue={formData.maxLeverage || 100} min={1} max={2000} step={1}
          onChange={e => set('maxLeverage', e.target.value)}
          onReset={() => set('maxLeverage', '')}
          onDirectInput={v => set('maxLeverage', String(v))} />
        <SliderField
          label={t('userOverride.maxLotSize')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.maxLotSize}
          displayValue={formData.maxLotSize ? `${formData.maxLotSize} ${t('userOverride.lots')}` : gd}
          sliderValue={formData.maxLotSize || 10} min={0.01} max={500} step={0.01}
          onChange={e => set('maxLotSize', e.target.value)}
          onReset={() => set('maxLotSize', '')}
          onDirectInput={v => set('maxLotSize', String(v))} />
        <SliderField
          label={t('userOverride.minLotSize')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.minLotSize}
          displayValue={formData.minLotSize ? `${formData.minLotSize} ${t('userOverride.lots')}` : gd}
          sliderValue={formData.minLotSize || 0.01} min={0.01} max={10} step={0.01}
          onChange={e => set('minLotSize', e.target.value)}
          onReset={() => set('minLotSize', '')}
          onDirectInput={v => set('minLotSize', String(v))} />
        <SliderField
          label={t('userOverride.lotStep')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.lotStep}
          displayValue={formData.lotStep ? `${formData.lotStep}` : gd}
          sliderValue={formData.lotStep || 0.01} min={0.001} max={1} step={0.001}
          onChange={e => set('lotStep', e.target.value)}
          onReset={() => set('lotStep', '')}
          onDirectInput={v => set('lotStep', String(v))} />
        {minAboveMax && (
          <div className="py-2">
            <p className="text-xs flex items-center gap-1" style={{ color: 'var(--accent-danger, #ef4444)' }}>
              {t('userOverride.minAboveMaxError')}
            </p>
          </div>
        )}
        <SliderField
          label={t('userOverride.marginCallLevel')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.marginCallLevel}
          displayValue={formData.marginCallLevel ? `${formData.marginCallLevel}%` : gd}
          sliderValue={formData.marginCallLevel || 80} min={0} max={150} step={5}
          onChange={e => set('marginCallLevel', e.target.value)}
          onReset={() => set('marginCallLevel', '')}
          onDirectInput={v => set('marginCallLevel', String(v))}
          accentVar="--accent-warning, #f59e0b" />
        <SliderField
          label={t('userOverride.stopOutLevel')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.stopOutLevel}
          displayValue={formData.stopOutLevel ? `${formData.stopOutLevel}%` : gd}
          sliderValue={formData.stopOutLevel || 50} min={0} max={100} step={5}
          onChange={e => set('stopOutLevel', e.target.value)}
          onReset={() => set('stopOutLevel', '')}
          onDirectInput={v => set('stopOutLevel', String(v))}
          accentVar="--accent-danger, #ef4444" />
        <SliderField
          label={t('userOverride.maxOpenPositions')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.maxOpenPositions}
          displayValue={formData.maxOpenPositions || gd}
          sliderValue={formData.maxOpenPositions || 200} min={1} max={500} step={1}
          onChange={e => set('maxOpenPositions', e.target.value)}
          onReset={() => set('maxOpenPositions', '')}
          onDirectInput={v => set('maxOpenPositions', String(v))} />
      </div>
    </div>
  );

  if (activeSection === 'pricing') return (
    <div>
      <h3 className="text-lg font-semibold mb-1">{t('userOverride.sectionPricingOverrides')}</h3>
      <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
        {t('userOverride.infoBannerDesc')}
      </p>
      <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
        <SliderField
          label={t('userOverride.commissionPerLot')}
          hint={t('userOverride.commissionPerLotHint')}
          resetLabel={t('userOverride.resetToGroupDefault')}
          value={formData.commissionPerLot}
          displayValue={formData.commissionPerLot ? `$${formData.commissionPerLot}` : gd}
          sliderValue={formData.commissionPerLot || 0} min={0} max={50} step={0.5}
          onChange={e => set('commissionPerLot', e.target.value)}
          onReset={() => set('commissionPerLot', '')}
          onDirectInput={v => set('commissionPerLot', String(v))} />
      </div>

      {existingOverride && (
        <div className="rounded-xl px-3 py-2.5 mt-5 flex gap-2"
          style={{
            background: 'color-mix(in srgb, var(--accent-warning,#f59e0b) 8%, transparent)',
            border: '1px solid color-mix(in srgb, var(--accent-warning,#f59e0b) 20%, transparent)',
          }}>
          <span className="text-base leading-none mt-0.5">⚠️</span>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary, #6b7280)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{t('userOverride.currentOverrideTitle')}</strong>{' '}
            {t('userOverride.currentOverrideDesc')}
          </p>
        </div>
      )}
    </div>
  );

  return null;
}

/* ─── Counts active overrides per section for badges ─── */
function useActiveCounts(formData) {
  const trading = formData.tradingDisabled === 1 ? 1 : 0;
  const risk = [
    formData.maxLeverage, formData.maxLotSize, formData.minLotSize,
    formData.lotStep, formData.marginCallLevel, formData.stopOutLevel,
    formData.maxOpenPositions,
  ].filter(v => v !== '' && v !== null && v !== undefined).length;
  const pricing = [formData.commissionPerLot].filter(v => v !== '' && v !== null && v !== undefined).length;
  return { trading, risk, pricing };
}

/* ─── Main modal ─── */
export default function UserOverrideModal({ user, onClose, onSave, existingOverride = null }) {
  const { t } = useLocale();
  const [loading, setLoading]           = useState(false);
  const [activeSection, setActiveSection] = useState('trading');
  const [formData, setFormData]         = useState(() => mapOverrideToForm(existingOverride));

  useEffect(() => { setFormData(mapOverrideToForm(existingOverride)); }, [existingOverride]);

  const set = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const minAboveMax =
    formData.minLotSize !== '' && formData.maxLotSize !== '' &&
    parseFloat(formData.minLotSize) > parseFloat(formData.maxLotSize);

  const counts = useActiveCounts(formData);
  const totalActive = counts.trading + counts.risk + counts.pricing;

  const SECTIONS = [
    { id: 'trading', label: t('userOverride.sectionTradingControl'), icon: '🛡️', count: counts.trading,  accent: 'var(--accent-danger,#ef4444)' },
    { id: 'risk',    label: t('userOverride.sectionRiskParameters'), icon: '⚠️', count: counts.risk,     accent: 'var(--accent-warning,#f59e0b)' },
    { id: 'pricing', label: t('userOverride.sectionPricingOverrides'), icon: '💰', count: counts.pricing, accent: 'var(--accent-primary,#3b82f6)' },
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (minAboveMax) return;
    setLoading(true);
    try {
      await onSave({
        max_leverage:       formData.maxLeverage      === '' ? null : parseInt(formData.maxLeverage),
        max_lot_size:       formData.maxLotSize       === '' ? null : parseFloat(formData.maxLotSize),
        min_lot_size:       formData.minLotSize       === '' ? null : parseFloat(formData.minLotSize),
        lot_step:           formData.lotStep          === '' ? null : parseFloat(formData.lotStep),
        margin_call_level:  formData.marginCallLevel  === '' ? null : parseFloat(formData.marginCallLevel),
        stop_out_level:     formData.stopOutLevel     === '' ? null : parseFloat(formData.stopOutLevel),
        max_open_positions: formData.maxOpenPositions === '' ? null : parseInt(formData.maxOpenPositions),
        commission_per_lot: formData.commissionPerLot === '' ? null : parseFloat(formData.commissionPerLot),
        trading_disabled:   formData.tradingDisabled ? 1 : 0,
      });
      toast.success(t('userOverride.saveSuccess'));
      onClose();
    } catch (err) {
      toast.error(err.message || t('userOverride.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const initials = `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase();

  return (
    <>
      <style>{`
        .ov-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          box-shadow: 0 1px 4px rgba(0,0,0,.25), 0 0 0 3px color-mix(in srgb, var(--accent-primary,#3b82f6) 18%, transparent);
          border: 2.5px solid #fff; cursor: pointer; transition: transform .1s;
        }
        .ov-thumb:active::-webkit-slider-thumb { transform: scale(1.15); }
        .ov-thumb::-moz-range-thumb {
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--accent-primary, #3b82f6);
          border: none; cursor: pointer;
        }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }

        @media (max-width: 767px) {
          .ov-backdrop { align-items: flex-end; }
          .ov-dialog { width: 100%; max-width: 100%; border-radius: 20px 20px 0 0; max-height: 92dvh; flex-direction: column; animation: ovSlideUp .28s cubic-bezier(.32,.72,0,1); }
          .ov-handle   { display: flex; }
          .ov-sidebar  { display: none; }
          .ov-tabbar   { display: flex; }
          .ov-footer   { padding: 12px 16px; padding-bottom: max(16px, env(safe-area-inset-bottom)); }
          .ov-footer button { padding-top: 14px; padding-bottom: 14px; border-radius: 16px; font-size: 16px; font-weight: 600; }
          .ov-toggle-row   { display: flex; }
          .ov-checkbox-row { display: none; }
        }
        @media (min-width: 768px) {
          .ov-backdrop { align-items: center; justify-content: center; padding: 16px; }
          .ov-dialog { width: 100%; max-width: 896px; border-radius: 16px; max-height: 90vh; flex-direction: row; border: 1px solid var(--border-color); animation: ovFadeIn .22s ease; }
          .ov-handle   { display: none; }
          .ov-sidebar  { display: flex; }
          .ov-tabbar   { display: none; }
          .ov-footer   { padding: 24px; }
          .ov-footer button { padding: 12px 24px; border-radius: 8px; font-size: 14px; font-weight: 500; }
          .ov-toggle-row   { display: none; }
          .ov-checkbox-row { display: flex; }
        }
        @keyframes ovSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes ovFadeIn {
          from { transform: translateY(6px) scale(0.985); opacity: 0; }
          to   { transform: translateY(0)   scale(1);     opacity: 1; }
        }
      `}</style>

      <div className="ov-backdrop fixed inset-0 z-50 flex"
        style={{ background: 'rgba(0,0,0,.5)', backdropFilter: 'blur(4px)' }}
        onClick={e => e.target === e.currentTarget && onClose()}>

        <div className="ov-dialog flex overflow-hidden" style={{ background: 'var(--bg-primary, #fff)' }}>

          {/* ════ Desktop sidebar ════ */}
          <div className="ov-sidebar w-56 flex-col flex-shrink-0 overflow-y-auto p-4"
            style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)' }}>

            {/* User identity card */}
            <div className="flex flex-col items-center text-center px-2 pt-3 pb-5 mb-3 border-b"
              style={{ borderColor: 'var(--border-color)' }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-black"
                style={{
                  background: 'linear-gradient(135deg, var(--accent-primary,#3b82f6), color-mix(in srgb, var(--accent-primary,#3b82f6) 60%, var(--accent-info,#06b6d4)))',
                  color: '#fff',
                  boxShadow: '0 4px 16px color-mix(in srgb, var(--accent-primary,#3b82f6) 30%, transparent)',
                }}>
                {initials || <Shield className="w-6 h-6" />}
              </div>
              <p className="mt-3 text-sm font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
                {user.first_name} {user.last_name}
              </p>
              <p className="mt-0.5 text-[11px] truncate w-full" style={{ color: 'var(--text-tertiary)' }}>
                {user.email}
              </p>
            </div>

            <h3 className="text-xs font-semibold mb-2 px-2 uppercase tracking-wider"
              style={{ color: 'var(--text-secondary)' }}>
              {t('symbolSettings.sectionsLabel') ?? 'Sections'}
            </h3>
            <div className="space-y-1">
              {SECTIONS.map(s => (
                <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                  className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between"
                  style={{
                    background: activeSection === s.id ? 'var(--accent-primary)' : 'transparent',
                    color: activeSection === s.id ? '#fff' : 'var(--text-secondary)',
                  }}>
                  <span><span className="mr-2">{s.icon}</span>{s.label}</span>
                  {s.count > 0 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                      style={{
                        background: activeSection === s.id ? 'rgba(255,255,255,0.25)' : `color-mix(in srgb, ${s.accent} 20%, transparent)`,
                        color: activeSection === s.id ? '#fff' : s.accent,
                      }}>
                      {s.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* ════ Main column ════ */}
          <div className="flex-1 flex flex-col overflow-hidden min-w-0">

            {/* Mobile drag handle */}
            <div className="ov-handle justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-[5px] rounded-full" style={{ background: 'var(--border-color, #d1d5db)' }} />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
              style={{ borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Shield className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
                    {t('userOverride.title')}
                  </h2>
                  {totalActive > 0 && (
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: 'color-mix(in srgb, var(--accent-warning,#f59e0b) 15%, transparent)', color: '#92400e' }}>
                      {totalActive} active
                    </span>
                  )}
                </div>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {user.first_name} {user.last_name} · {user.email}
                </p>
              </div>
              <button onClick={onClose}
                className="p-2 hover:bg-[var(--bg-secondary)] rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile tab bar */}
            <div className="ov-tabbar gap-1 px-3 py-2 flex-shrink-0 overflow-x-auto"
              style={{ borderBottom: '1px solid var(--border-color)' }}>
              {SECTIONS.map(({ id, label, icon, count, accent }) => (
                <button key={id} type="button" onClick={() => setActiveSection(id)}
                  className="flex-shrink-0 flex items-center gap-1.5 py-2 px-3 rounded-xl text-xs font-semibold transition-all active:scale-95"
                  style={{
                    background: activeSection === id ? 'var(--accent-primary, #3b82f6)' : 'var(--bg-secondary, #f3f4f6)',
                    color: activeSection === id ? '#fff' : 'var(--text-secondary, #6b7280)',
                  }}>
                  <span>{icon}</span>
                  {label}
                  {count > 0 && (
                    <span className="text-xs px-1.5 rounded-full"
                      style={{
                        background: activeSection === id ? 'rgba(255,255,255,0.3)' : `color-mix(in srgb, ${accent} 25%, transparent)`,
                        color: activeSection === id ? '#fff' : accent,
                      }}>
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Info banner */}
            <div className="mx-6 mt-4 flex-shrink-0">
              <div className="rounded-xl px-3 py-2.5 flex gap-2"
                style={{
                  background: 'color-mix(in srgb, var(--accent-primary,#3b82f6) 8%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--accent-primary,#3b82f6) 20%, transparent)',
                }}>
                <span className="text-base leading-none mt-0.5">ℹ️</span>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                  <strong style={{ color: 'var(--text-primary)' }}>{t('userOverride.infoBannerTitle')}</strong>{' '}
                  {t('userOverride.infoBannerDesc')}
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5" style={{ WebkitOverflowScrolling: 'touch' }}>
              <SectionContent
                activeSection={activeSection}
                formData={formData}
                set={set}
                t={t}
                minAboveMax={minAboveMax}
                existingOverride={existingOverride}
              />
              <div className="h-2" />
            </div>

            {/* Footer */}
            <div className="ov-footer flex gap-3 flex-shrink-0"
              style={{ borderTop: '1px solid var(--border-color, #e5e7eb)' }}>
              <button type="button" onClick={onClose} disabled={loading}
                className="flex-1 bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50">
                {t('common.cancel')}
              </button>
              <button type="button" onClick={handleSubmit} disabled={loading || minAboveMax}
                className="flex-1 bg-[var(--accent-primary)] text-white hover:bg-[var(--accent-primary-hover)] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t('userOverride.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('userOverride.saveOverride')}
                  </>
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}